import { useState, useEffect } from "react";
import API, { formatWIB } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { ArrowRight, ArrowLeft, CheckCircle, CheckSquare, Square, CaretLeft, CaretRight, FunnelSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { useTableSort } from "../hooks/useTableSort";

function formatIDR(num) {
  if (!num && num !== 0) return "-";
  return "Rp " + new Intl.NumberFormat("id-ID").format(num);
}

export default function UnresolvedQueuePage() {
  const [view, setView] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Filters for jobs list
  const [filterSearch, setFilterSearch] = useState("");

  // Records within a job
  const [activeJob, setActiveJob] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsTotal, setRecordsTotal] = useState(0);

  // Filters for records
  const [recFilterType, setRecFilterType] = useState("all");
  const [recFilterStatus, setRecFilterStatus] = useState("all");
  const [recFilterSearch, setRecFilterSearch] = useState("");

  // Selection & actions
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolveNotes, setResolveNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rematching, setRematching] = useState(false);

  const perPage = 20;

  useEffect(() => { loadGrouped(); }, [page]);

  const loadGrouped = async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * perPage;
      const { data } = await API.get(`/records/unresolved/grouped?limit=${perPage}&skip=${skip}`);
      setJobs(data.jobs);
      setTotalJobs(data.total_jobs);
      setTotalRecords(data.total_records);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openJob = async (jobData) => {
    setActiveJob(jobData);
    setView("records");
    setBulkSelected(new Set());
    setRecFilterType("all");
    setRecFilterStatus("all");
    setRecFilterSearch("");
    try {
      const { data } = await API.get(`/records/unresolved/job/${jobData.job.id}?limit=200`);
      setRecords(data.records);
      setRecordsTotal(data.total);
    } catch (e) { console.error(e); }
  };

  const handleReMatch = async (jobId) => {
    setRematching(true);
    try {
      const { data } = await API.post("/records/rematch", { job_id: jobId, lookback_days: 3 });
      alert(`Re-match selesai: ${data.matched} cocok, ${data.still_unresolved} masih unresolved`);
      if (activeJob) await openJob(activeJob);
      loadGrouped();
    } catch (e) { alert(e.response?.data?.detail || "Re-match gagal"); }
    finally { setRematching(false); }
  };

  const handleMarkResolved = async () => {
    const ids = bulkSelected.size > 0 ? [...bulkSelected] : [];
    if (ids.length === 0) return;
    setSubmitting(true);
    try {
      await API.post("/records/mark-resolved", { record_ids: ids, notes: resolveNotes || "Resolved by admin" });
      setBulkSelected(new Set());
      setShowResolveDialog(false);
      setResolveNotes("");
      if (activeJob) await openJob(activeJob);
      loadGrouped();
    } catch (e) { alert("Gagal mark resolved"); }
    finally { setSubmitting(false); }
  };

  const handleMarkAllResolved = async () => {
    if (!window.confirm("Tandai SEMUA unresolved di job ini sebagai Resolved?")) return;
    setSubmitting(true);
    try {
      const ids = records.map(r => r.id);
      await API.post("/records/mark-resolved", { record_ids: ids, notes: "Bulk resolved all" });
      if (activeJob) await openJob(activeJob);
      loadGrouped();
    } catch (e) { alert("Gagal mark resolved"); }
    finally { setSubmitting(false); }
  };

  const toggleBulk = (id) => {
    setBulkSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  // Filtered jobs
  const filteredJobs = filterSearch
    ? jobs.filter(j => {
        const q = filterSearch.toLowerCase();
        return (j.job.site_name || "").toLowerCase().includes(q) ||
               (j.job.date_from || "").includes(q) || (j.job.date_to || "").includes(q) ||
               (j.job.id || "").toLowerCase().includes(q);
      })
    : jobs;

  // Sortable headers — persisted ke localStorage, support multi-sort (shift+click)
  const sortAccessor = (rec, key) => {
    switch (key) {
      case "username": return (rec.username || "").toLowerCase();
      case "transaction_type": return (rec.transaction_type || "").toLowerCase();
      case "amount": return Number(rec.amount) || 0;
      case "fund_method": return (rec.fund_method || "").toLowerCase();
      case "final_status": return (rec.final_status || "").toLowerCase();
      case "transaction_date": return rec.transaction_date || "";
      case "notes": return (rec.notes || "").toLowerCase();
      default: return "";
    }
  };
  const { sortBy, clearSort, SortHeader, applySorts } = useTableSort({
    storageKey: "unresolved-queue-sort",
    accessor: sortAccessor,
    testIdPrefix: "unresolved-sort-header",
  });

  // Filtered records
  const filteredRecordsRaw = records.filter(r => {
    if (recFilterType !== "all" && r.transaction_type !== recFilterType) return false;
    if (recFilterStatus !== "all" && r.final_status !== recFilterStatus) return false;
    if (recFilterSearch) {
      const q = recFilterSearch.toLowerCase();
      if (!(r.username || "").toLowerCase().includes(q) && !(r.account_name || "").toLowerCase().includes(q) &&
          !(r.fund_method || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const filteredRecords = applySorts(filteredRecordsRaw);

  const totalPages = Math.max(1, Math.ceil(totalJobs / perPage));

  // ============ RECORDS VIEW ============
  if (view === "records" && activeJob) {
    return (
      <div data-testid="unresolved-records" className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => { setView("jobs"); setActiveJob(null); setBulkSelected(new Set()); }} className="rounded-sm gap-1">
              <ArrowLeft size={14} /> Semua Jobs
            </Button>
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">Unresolved: {activeJob.job.site_name || "Job"}</h2>
              <p className="text-xs font-mono text-zinc-400">{activeJob.job.id?.slice(0,8)} | {activeJob.job.date_from} ~ {activeJob.job.date_to} | {recordsTotal} records</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="rematch-btn" onClick={() => handleReMatch(activeJob.job.id)} disabled={rematching} variant="outline"
              className="rounded-sm gap-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-50">
              <MagnifyingGlass size={14} /> {rematching ? "Re-matching..." : "Re-Match (±3 hari)"}
            </Button>
          </div>
        </div>

        {/* Filter + Bulk Actions Bar */}
        <div className="border border-zinc-200 rounded-sm p-3 space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Type quick toggle */}
              <div data-testid="unresolved-filter-type" className="flex items-center gap-1">
                {(() => {
                  const counts = {
                    all: records.length,
                    deposit: records.filter(r => r.transaction_type === "deposit").length,
                    withdraw: records.filter(r => r.transaction_type === "withdraw").length,
                  };
                  const tabs = [
                    { k: "all", label: "Semua", color: "zinc" },
                    { k: "deposit", label: "Deposit", color: "emerald" },
                    { k: "withdraw", label: "Withdraw", color: "amber" },
                  ];
                  return tabs.map(t => {
                    const active = recFilterType === t.k;
                    const cm = {
                      zinc: "border-zinc-400 bg-zinc-700 text-white",
                      emerald: "border-emerald-400 bg-emerald-600 text-white",
                      amber: "border-amber-400 bg-amber-600 text-white",
                    };
                    return (
                      <button key={t.k}
                        data-testid={`unresolved-type-${t.k}`}
                        onClick={() => setRecFilterType(t.k)}
                        disabled={counts[t.k] === 0 && t.k !== "all"}
                        className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${active ? cm[t.color] : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"} ${counts[t.k] === 0 && t.k !== "all" ? "opacity-40 cursor-not-allowed" : ""}`}>
                        {t.label} <span className="font-mono font-semibold ml-1">({counts[t.k]})</span>
                      </button>
                    );
                  });
                })()}
              </div>
              <div className="h-5 w-px bg-zinc-300" />
              {/* Status quick toggle */}
              <div data-testid="unresolved-filter-status" className="flex items-center gap-1">
                {(() => {
                  const counts = {
                    all: records.length,
                    not_found: records.filter(r => r.final_status === "not_found").length,
                    panel_error: records.filter(r => r.final_status === "panel_error").length,
                    sheet_error: records.filter(r => r.final_status === "sheet_error").length,
                  };
                  const tabs = [
                    { k: "all", label: "Semua Status", color: "zinc" },
                    { k: "not_found", label: "Not Found", color: "red" },
                    { k: "panel_error", label: "Panel Error", color: "orange" },
                    { k: "sheet_error", label: "Sheet Error", color: "amber" },
                  ];
                  return tabs.map(t => {
                    const active = recFilterStatus === t.k;
                    const cm = {
                      zinc: "border-zinc-400 bg-zinc-700 text-white",
                      red: "border-red-400 bg-red-600 text-white",
                      orange: "border-orange-400 bg-orange-600 text-white",
                      amber: "border-amber-400 bg-amber-600 text-white",
                    };
                    return (
                      <button key={t.k}
                        data-testid={`unresolved-status-${t.k}`}
                        onClick={() => setRecFilterStatus(t.k)}
                        disabled={counts[t.k] === 0 && t.k !== "all"}
                        className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${active ? cm[t.color] : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"} ${counts[t.k] === 0 && t.k !== "all" ? "opacity-40 cursor-not-allowed" : ""}`}>
                        {t.label} <span className="font-mono font-semibold ml-1">({counts[t.k]})</span>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => { setShowResolveDialog(true); setResolveNotes(""); }} disabled={bulkSelected.size === 0}
                variant="outline" className="rounded-sm gap-1 text-xs h-8">
                <CheckCircle size={12} /> Resolved {bulkSelected.size > 0 ? `(${bulkSelected.size})` : ""}
              </Button>
              <Button onClick={handleMarkAllResolved} disabled={submitting || records.length === 0}
                variant="outline" className="rounded-sm gap-1 text-xs h-8 border-zinc-400">
                <CheckCircle size={12} /> Resolved All
              </Button>
            </div>
          </div>
          {/* Search input + counter */}
          <div className="flex items-center gap-2">
            <Input value={recFilterSearch} onChange={e => setRecFilterSearch(e.target.value)}
              placeholder="Cari username, account name, bank..." className="rounded-sm border-zinc-300 h-8 text-xs w-72" />
            <span className="text-[10px] text-zinc-500 font-mono">
              Menampilkan {filteredRecords.length} dari {records.length} record
              {bulkSelected.size > 0 && <span className="text-violet-600 font-semibold ml-2">· {bulkSelected.size} dipilih</span>}
            </span>
            {sortBy.length > 0 && (
              <button data-testid="unresolved-sort-reset" onClick={clearSort}
                className="text-[10px] font-mono text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-sm px-2 py-0.5 ml-auto"
                title="Reset semua sort">
                ⤺ Reset Sort ({sortBy.length})
              </button>
            )}
          </div>
        </div>

        <div className="border border-zinc-200 rounded-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="px-2 py-2 w-8">
                  <button onClick={() => setBulkSelected(prev => prev.size === filteredRecords.length ? new Set() : new Set(filteredRecords.map(r => r.id)))}>
                    {bulkSelected.size > 0 && bulkSelected.size === filteredRecords.length ? <CheckSquare size={14} weight="fill" className="text-zinc-900" /> : <Square size={14} className="text-zinc-400" />}
                  </button>
                </th>
                <SortHeader k="username" label="Username" />
                <SortHeader k="transaction_type" label="Tipe" />
                <SortHeader k="amount" label="Nominal" align="right" />
                <SortHeader k="fund_method" label="Fund Method" />
                <SortHeader k="final_status" label="Status" />
                <SortHeader k="transaction_date" label="Tanggal" />
                <SortHeader k="notes" label="Notes" />
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-zinc-400">
                  {records.length === 0 ? "Semua record sudah resolved" : "Tidak ada record sesuai filter"}
                </td></tr>
              ) : filteredRecords.map((rec) => (
                <tr key={rec.id} className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${bulkSelected.has(rec.id) ? "bg-blue-50" : ""}`}>
                  <td className="px-2 py-2">
                    <button onClick={() => toggleBulk(rec.id)}>
                      {bulkSelected.has(rec.id) ? <CheckSquare size={14} weight="fill" className="text-zinc-900" /> : <Square size={14} className="text-zinc-400" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-zinc-900">{rec.username}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm ${rec.transaction_type === "deposit" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                      {rec.transaction_type === "deposit" ? "DEPO" : "WD"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-right">{formatIDR(rec.amount)}</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-500 truncate max-w-[120px]">{rec.fund_method || "-"}</td>
                  <td className="px-3 py-2"><StatusBadge status={rec.final_status} /></td>
                  <td className="px-3 py-2 text-[10px] font-mono text-zinc-500">{rec.transaction_date}</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-400 truncate max-w-[100px]">{rec.notes || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Resolve Dialog */}
        <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Mark as Resolved</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-xs text-zinc-600">{bulkSelected.size} record akan ditandai sebagai "Resolved".</p>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Catatan / Alasan</Label>
                <Textarea value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder="Contoh: Data sudah diperbaiki di Sheet, atau transaksi batal" className="rounded-sm border-zinc-300 text-xs h-20" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleMarkResolved} disabled={submitting} className="flex-1 bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm">
                  {submitting ? "Memproses..." : "Mark Resolved"}
                </Button>
                <Button variant="outline" onClick={() => setShowResolveDialog(false)} className="rounded-sm">Batal</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============ JOBS LIST VIEW ============
  return (
    <div data-testid="unresolved-queue-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Unresolved Queue</h1>
          <p className="text-sm text-zinc-500 mt-1">{totalRecords} records di {totalJobs} job belum terselesaikan</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="border border-zinc-200 rounded-sm px-3 py-2 flex items-center gap-3">
        <FunnelSimple size={16} className="text-zinc-400" />
        <Input data-testid="unresolved-filter-search" value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
          placeholder="Cari site, tanggal, ID job..." className="rounded-sm border-zinc-300 h-8 text-xs flex-1 max-w-xs" />
        <span className="text-[10px] text-zinc-400 font-mono">{filteredJobs.length} / {jobs.length} jobs</span>
        {filterSearch && (
          <button onClick={() => setFilterSearch("")} className="text-xs text-red-500 hover:text-red-700">Reset</button>
        )}
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-zinc-400">Loading...</div>
      ) : filteredJobs.length === 0 ? (
        <div className="border border-zinc-200 rounded-sm p-12 text-center">
          <CheckCircle size={32} className="text-emerald-400 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">{filterSearch ? "Tidak ada job yang sesuai filter." : "Semua record terselesaikan."}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredJobs.map((item) => (
            <div key={item.job.id} data-testid={`unresolved-job-${item.job.id?.slice(0,8)}`}
              className="border border-zinc-200 rounded-sm p-4 hover:border-zinc-300 transition-colors cursor-pointer" onClick={() => openJob(item)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-red-100 flex items-center justify-center text-red-700 text-sm font-bold font-mono">
                    {item.unresolved_count}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900">{item.job.site_name || "Job"} — {item.job.date_from} ~ {item.job.date_to}</h3>
                    <p className="text-[10px] font-mono text-zinc-400">ID: {item.job.id?.slice(0,8)} | Tipe: {item.job.record_type || "all"} | Total: {item.job.total_records} records</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs font-mono text-red-600 font-semibold">{item.unresolved_count} unresolved</p>
                    <p className="text-[10px] text-zinc-400">{formatIDR(item.total_amount)}</p>
                  </div>
                  <ArrowRight size={16} className="text-zinc-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalJobs > perPage && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-zinc-500 font-mono">{(page-1)*perPage + 1}-{Math.min(page*perPage, totalJobs)} dari {totalJobs} jobs</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1} className="rounded-sm h-7 w-7 p-0"><CaretLeft size={14} /></Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages} className="rounded-sm h-7 w-7 p-0"><CaretRight size={14} /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
